import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { SelectField, SwitchField, TextField } from '@/components/form-fields';
import {
  AddButton,
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
  SettingsSkeleton,
} from '@/components/settings/settings-kit';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import {
  bpsToPercent,
  deleteJurisdiction,
  getTaxSettings,
  type JurisdictionRecord,
  listJurisdictions,
  percentToBps,
  saveJurisdiction,
  saveTaxSettings,
  type TaxSettings as TaxSettingsValues,
} from '@/lib/settings/fleet';
import { useForm } from '@/lib/use-form';

/**
 * Taxes.
 *
 * Rates are stored in basis points and typed as percentages, and the
 * conversion happens once, here -- `resolveTaxRates` and every receipt read
 * the bps, so a percentage that round-trips through a text input is the only
 * place a fractional rate can be lost.
 *
 * The excise rate is per account (one state, one rate); sales and local
 * cannabis tax are per jurisdiction, because a store that delivers across a
 * city line collects two different local rates.
 */

const percent = z
  .string()
  .trim()
  .regex(/^\d{1,3}(\.\d{1,2})?$/, 'A rate looks like 8.5');

const settingsSchema = z.object({
  excise: percent,
  pricesIncludeExciseTax: z.boolean(),
  pricesIncludeCannabisTax: z.boolean(),
  pricesIncludeSalesTax: z.boolean(),
  roundingMode: z.enum(['half_up', 'bankers']),
});

type SettingsValues = z.infer<typeof settingsSchema>;

const jurisdictionSchema = z.object({
  name: z.string().trim().min(1, 'Name the jurisdiction').max(120),
  salesTax: percent,
  cannabisTax: percent,
  medicalCannabisTax: percent,
  notes: z.string().trim().max(500),
  active: z.boolean(),
});

type JurisdictionValues = z.infer<typeof jurisdictionSchema>;

const EMPTY_JURISDICTION: JurisdictionValues = {
  name: '',
  salesTax: '0',
  cannabisTax: '0',
  medicalCannabisTax: '0',
  notes: '',
  active: true,
};

const ROUNDING_OPTIONS = [
  { value: 'half_up', label: 'Half up (0.5 rounds away from zero)' },
  { value: 'bankers', label: "Banker's (0.5 rounds to even)" },
];

export function TaxSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('taxes.manage');
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<JurisdictionRecord | 'new' | null>(null);

  const settings = useQuery({
    queryKey: ['tax-settings', accountId],
    queryFn: () => getTaxSettings(accountId),
  });

  const jurisdictions = useQuery({
    queryKey: ['tax-jurisdictions', accountId],
    queryFn: () => listJurisdictions(accountId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['tax-settings', accountId] });
    queryClient.invalidateQueries({ queryKey: ['tax-jurisdictions', accountId] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => deleteJurisdiction(accountId, id),
    onSuccess: () => {
      toast.success('Jurisdiction removed.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (settings.isLoading || jurisdictions.isLoading) return <SettingsSkeleton />;

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="taxes.manage" />}

      <TaxSettingsForm
        accountId={accountId}
        canManage={canManage}
        values={settings.data ?? null}
        onSaved={refresh}
      />

      <SectionCard
        title="Jurisdictions"
        description="Sales and local cannabis tax, per city or county you serve. A store and a delivery zone each point at one."
        action={
          <AddButton onClick={() => setEditing('new')} disabled={!canManage}>
            Add
          </AddButton>
        }
      >
        {jurisdictions.data?.length ? (
          <ListShell>
            {jurisdictions.data.map((jurisdiction) => (
              <ListRow
                key={jurisdiction.id}
                title={jurisdiction.name}
                subtitle={`Sales ${bpsToPercent(jurisdiction.salesTaxBps)}% · Cannabis ${bpsToPercent(
                  jurisdiction.cannabisTaxBps,
                )}% · Medical ${bpsToPercent(jurisdiction.medicalCannabisTaxBps)}%${
                  jurisdiction.active ? '' : ' · inactive'
                }`}
                onClick={canManage ? () => setEditing(jurisdiction) : undefined}
                selected={typeof editing === 'object' && editing?.id === jurisdiction.id}
                actions={
                  canManage ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(jurisdiction.id)}
                    >
                      Remove
                    </Button>
                  ) : null
                }
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>
            No jurisdictions yet, so only the excise rate is collected. Add the city or county you
            sell in.
          </EmptyState>
        )}

        {editing && (
          <JurisdictionForm
            accountId={accountId}
            record={editing === 'new' ? null : editing}
            onDone={() => {
              setEditing(null);
              refresh();
            }}
            onCancel={() => setEditing(null)}
          />
        )}
      </SectionCard>
    </div>
  );
}

function TaxSettingsForm({
  accountId,
  canManage,
  values: stored,
  onSaved,
}: {
  accountId: string;
  canManage: boolean;
  values: TaxSettingsValues | null;
  onSaved: () => void;
}) {
  const initial: SettingsValues = {
    excise: bpsToPercent(stored?.cannabisExciseTaxBps ?? 1500),
    pricesIncludeExciseTax: stored?.pricesIncludeExciseTax ?? true,
    pricesIncludeCannabisTax: stored?.pricesIncludeCannabisTax ?? true,
    pricesIncludeSalesTax: stored?.pricesIncludeSalesTax ?? true,
    roundingMode: stored?.roundingMode ?? 'half_up',
  };

  const { values, errors, isDirty, set, reset, submit } = useForm(initial, settingsSchema);

  useEffect(() => {
    reset(initial);
    // The stored record is the only thing that should rebase the form.
  }, [stored, reset]);

  const save = useMutation({
    mutationFn: (parsed: SettingsValues) =>
      saveTaxSettings(accountId, {
        cannabisExciseTaxBps: percentToBps(parsed.excise),
        pricesIncludeExciseTax: parsed.pricesIncludeExciseTax,
        pricesIncludeCannabisTax: parsed.pricesIncludeCannabisTax,
        pricesIncludeSalesTax: parsed.pricesIncludeSalesTax,
        roundingMode: parsed.roundingMode,
      }),
    onSuccess: () => {
      toast.success('Tax settings saved.');
      onSaved();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form onSubmit={submit((parsed) => save.mutate(parsed))} noValidate>
      <SectionCard
        title="Rates and presentation"
        description="Excise is set by the state and applies everywhere you sell. Whether a listed price already contains a tax changes what the receipt breaks out, not what is owed."
      >
        <fieldset disabled={!canManage} className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="tax-excise"
              label="Cannabis excise tax"
              description="Percent. California is 15%."
              value={values.excise}
              error={errors.excise}
              onChange={(value) => set('excise', value)}
            />

            <SelectField
              id="tax-rounding"
              label="Rounding"
              description="How a half-cent is resolved on every line."
              value={values.roundingMode}
              options={ROUNDING_OPTIONS}
              error={errors.roundingMode}
              onChange={(value) => set('roundingMode', value as SettingsValues['roundingMode'])}
            />
          </div>

          <div className="flex flex-col gap-3">
            <SwitchField
              id="tax-include-excise"
              label="Listed prices include excise tax"
              checked={values.pricesIncludeExciseTax}
              onChange={(checked) => set('pricesIncludeExciseTax', checked)}
            />
            <SwitchField
              id="tax-include-cannabis"
              label="Listed prices include local cannabis tax"
              checked={values.pricesIncludeCannabisTax}
              onChange={(checked) => set('pricesIncludeCannabisTax', checked)}
            />
            <SwitchField
              id="tax-include-sales"
              label="Listed prices include sales tax"
              checked={values.pricesIncludeSalesTax}
              onChange={(checked) => set('pricesIncludeSalesTax', checked)}
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" disabled={!isDirty || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            {isDirty && (
              <Button type="button" variant="ghost" onClick={() => reset()}>
                Discard
              </Button>
            )}
          </div>
        </fieldset>
      </SectionCard>
    </form>
  );
}

function JurisdictionForm({
  accountId,
  record,
  onDone,
  onCancel,
}: {
  accountId: string;
  record: JurisdictionRecord | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const initial: JurisdictionValues = record
    ? {
        name: record.name,
        salesTax: bpsToPercent(record.salesTaxBps),
        cannabisTax: bpsToPercent(record.cannabisTaxBps),
        medicalCannabisTax: bpsToPercent(record.medicalCannabisTaxBps),
        notes: record.notes ?? '',
        active: record.active,
      }
    : EMPTY_JURISDICTION;

  const { values, errors, set, submit } = useForm(initial, jurisdictionSchema);

  const save = useMutation({
    mutationFn: (parsed: JurisdictionValues) =>
      saveJurisdiction(accountId, {
        id: record?.id,
        name: parsed.name,
        salesTaxBps: percentToBps(parsed.salesTax),
        cannabisTaxBps: percentToBps(parsed.cannabisTax),
        medicalCannabisTaxBps: percentToBps(parsed.medicalCannabisTax),
        notes: parsed.notes || null,
        active: parsed.active,
      }),
    onSuccess: () => {
      toast.success(record ? 'Jurisdiction updated.' : 'Jurisdiction added.');
      onDone();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <form
      onSubmit={submit((parsed) => save.mutate(parsed))}
      noValidate
      className="flex flex-col gap-4 rounded-lg border border-dashed p-4"
    >
      <TextField
        id="jurisdiction-name"
        label="Name"
        placeholder="Petaluma, CA"
        value={values.name}
        error={errors.name}
        onChange={(value) => set('name', value)}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          id="jurisdiction-sales"
          label="Sales tax %"
          value={values.salesTax}
          error={errors.salesTax}
          onChange={(value) => set('salesTax', value)}
        />
        <TextField
          id="jurisdiction-cannabis"
          label="Cannabis tax %"
          value={values.cannabisTax}
          error={errors.cannabisTax}
          onChange={(value) => set('cannabisTax', value)}
        />
        <TextField
          id="jurisdiction-medical"
          label="Medical cannabis tax %"
          description="Often zero: many jurisdictions exempt patients."
          value={values.medicalCannabisTax}
          error={errors.medicalCannabisTax}
          onChange={(value) => set('medicalCannabisTax', value)}
        />
      </div>

      <TextField
        id="jurisdiction-notes"
        label="Notes"
        value={values.notes}
        error={errors.notes}
        onChange={(value) => set('notes', value)}
      />

      <SwitchField
        id="jurisdiction-active"
        label="Active"
        checked={values.active}
        onChange={(checked) => set('active', checked)}
      />

      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : record ? 'Save jurisdiction' : 'Add jurisdiction'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
